import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useState } from "react";

import {
  type DashboardAccessibility,
  DashboardRecord,
} from "@/shared/domain/dashboards";
import { useCreateDashboard } from "@/shared/domain/dashboards/hooks";
import { Button } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import { DashboardDefinition } from "../DashboardHeader/DashboardParams";

import styles from "./DashboardConflictDialog.module.css";

export interface DashboardConflictDialogProps {
  /** The dashboard as the server holds it, taken from the conflict response. */
  serverRecord: DashboardRecord;
  /** The user's unsaved edits — what a copy would be created from. */
  currentDefinition: DashboardDefinition;
  /** Adopt the server's version, discarding the local edits. */
  onDiscardAndReload: (serverRecord: DashboardRecord) => void;
  /** The edits were persisted as a brand-new dashboard. */
  onSavedCopy: (created: DashboardRecord) => void;
}

function modifiedDescription(record: DashboardRecord): string {
  const when = new Date(record.modified);
  const stamp = Number.isNaN(when.getTime()) ? null : when.toLocaleString();
  return stamp === null
    ? `Last changed by ${record.modified_by}.`
    : `Last changed by ${record.modified_by} on ${stamp}.`;
}

/**
 * Shown when a save is rejected because someone else changed the dashboard
 * first. Both doors preserve the user's work in some form, so there is
 * deliberately no overwrite option — and no dismissal either: leaving the dialog
 * without choosing would strand the session on a stale base revision where every
 * later save conflicts again.
 */
export function DashboardConflictDialog({
  serverRecord,
  currentDefinition,
  onDiscardAndReload,
  onSavedCopy,
}: DashboardConflictDialogProps) {
  const [copyName, setCopyName] = useState(`${serverRecord.name} (copy)`);
  const [accessibility, setAccessibility] = useState<DashboardAccessibility>(
    serverRecord.accessibility,
  );
  // Dialog-owned so its pending/error state is discarded when the dialog
  // unmounts, matching SaveAsDialog.
  const createMutation = useCreateDashboard();

  const handleSaveCopy = () => {
    const name = copyName.trim();
    if (name === "") {
      return;
    }
    createMutation.mutate(
      {
        name,
        accessibility,
        dashboard_definition: currentDefinition,
      },
      { onSuccess: onSavedCopy },
    );
  };

  return (
    <Dialog open disableEscapeKeyDown onClose={() => {}}>
      <DialogTitle>This dashboard changed while you were editing</DialogTitle>
      <DialogContent>
        <div className={styles.dialogBody}>
          <p className={styles.explanation}>
            Someone else saved <strong>{serverRecord.name}</strong> after you
            opened it, so saving now would erase their changes.{" "}
            {modifiedDescription(serverRecord)}
          </p>
          <p className={styles.explanation}>
            Keep your edits by saving them as a separate dashboard, or discard
            them and load the current version.
          </p>
          <TextField
            label="Name for your copy"
            value={copyName}
            onChange={(e) => setCopyName(e.target.value)}
            size="small"
            disabled={createMutation.isPending}
          />
          <RadioGroup
            value={accessibility}
            onChange={(_e, value) =>
              setAccessibility(value as DashboardAccessibility)
            }
          >
            <FormControlLabel
              value="user"
              control={<Radio size="small" />}
              label="Personal (only you)"
            />
            <FormControlLabel
              value="organization"
              control={<Radio size="small" />}
              label="Shared with organization"
            />
          </RadioGroup>
          {createMutation.isError && (
            <Alert severity="error">
              {userFacingErrorMessage(
                createMutation.error,
                "Failed to save a copy. Please try again.",
              )}
            </Alert>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => onDiscardAndReload(serverRecord)}
          disabled={createMutation.isPending}
        >
          Discard my edits &amp; reload
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveCopy}
          disabled={createMutation.isPending || copyName.trim() === ""}
        >
          Save my edits as a copy
        </Button>
      </DialogActions>
    </Dialog>
  );
}
